import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { formatDate } from '../utils/formatters';
import { 
  Target, TrendingUp, Award, CheckCircle, Clock, AlertCircle, Star,
  Users, Plus, Search, Filter, BarChart3, ArrowRight, Edit2, Trash2,
  ChevronDown, Calendar, FileText, UserCheck, Activity, RefreshCw,
  X, Save, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function UnifiedPerformanceManagement({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('reviews');
  const [reviews, setReviews] = useState([]);
  const [goals, setGoals] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [reviewCycles, setReviewCycles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Stats
  const [stats, setStats] = useState({
    totalReviews: 0,
    completedReviews: 0,
    pendingReviews: 0,
    avgRating: 0,
    activeGoals: 0,
    achievedGoals: 0
  });

  // Form states
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewFormData, setReviewFormData] = useState({
    employee_id: '',
    review_cycle: 'Q4 2025',
    review_period: 'Q4 2025',
    overall_rating: 4,
    strengths: '',
    areas_for_improvement: '',
    goals_for_next_period: '',
    reviewer_comments: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [reviewsRes, goalsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/api/performance-reviews`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/goals`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/employees`, { headers }).catch(() => ({ data: [] }))
      ]);

      const reviewsData = Array.isArray(reviewsRes.data) ? reviewsRes.data : [];
      const goalsData = Array.isArray(goalsRes.data) ? goalsRes.data : [];
      
      setReviews(reviewsData);
      setGoals(goalsData);
      setEmployees(Array.isArray(employeesRes.data) ? employeesRes.data : []);

      // Calculate stats
      const completedReviews = reviewsData.filter(r => r.status === 'Completed' || r.status === 'Submitted').length;
      const pendingReviews = reviewsData.filter(r => r.status === 'Draft' || r.status === 'Pending').length;
      const avgRating = reviewsData.length > 0 
        ? (reviewsData.reduce((sum, r) => sum + (r.overall_rating || 0), 0) / reviewsData.length).toFixed(1)
        : 0;
      const activeGoals = goalsData.filter(g => g.status === 'In Progress' || g.status === 'Not Started').length;
      const achievedGoals = goalsData.filter(g => g.status === 'Achieved' || g.status === 'Completed').length;

      setStats({
        totalReviews: reviewsData.length,
        completedReviews,
        pendingReviews,
        avgRating,
        activeGoals,
        achievedGoals
      });

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (reviewId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/performance-reviews/${reviewId}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAllData();
    } catch (error) {
      console.error('Error submitting review:', error);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/performance-reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAllData();
    } catch (error) {
      console.error('Error deleting review:', error);
    }
  };

  const handleCreateReview = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/performance-reviews`, reviewFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowReviewForm(false);
      setReviewFormData({
        employee_id: '',
        review_cycle: 'Q4 2025',
        review_period: 'Q4 2025',
        overall_rating: 4,
        strengths: '',
        areas_for_improvement: '',
        goals_for_next_period: '',
        reviewer_comments: ''
      });
      fetchAllData();
    } catch (error) {
      console.error('Error creating review:', error);
    }
  };

  // Helper functions
  const getRatingColor = (rating) => {
    const r = parseFloat(rating);
    if (r >= 4.5) return 'text-green-600';
    if (r >= 3.5) return 'text-blue-600';
    if (r >= 2.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRatingBg = (rating) => {
    const r = parseFloat(rating);
    if (r >= 4.5) return 'bg-green-100';
    if (r >= 3.5) return 'bg-blue-100';
    if (r >= 2.5) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Completed': 'bg-green-100 text-green-700',
      'Submitted': 'bg-blue-100 text-blue-700',
      'Draft': 'bg-gray-100 text-gray-700',
      'Pending': 'bg-yellow-100 text-yellow-700',
      'In Progress': 'bg-purple-100 text-purple-700',
      'Achieved': 'bg-green-100 text-green-700',
      'Not Started': 'bg-gray-100 text-gray-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const filteredReviews = reviews.filter(review => {
    const matchesSearch = review.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          review.review_period?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || review.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Stat Card Component
  const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-xl" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>{value}</h3>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-screen bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#2b3380' }}></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>Performance Management</h1>
            <p className="text-gray-500">Track employee performance reviews and goals</p>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={fetchAllData}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button 
              onClick={() => setShowReviewForm(true)}
              className="px-4 py-2 text-white rounded-lg flex items-center gap-2"
              style={{ backgroundColor: '#2b3380' }}
            >
              <Plus className="w-4 h-4" />
              New Review
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard
            title="Total Reviews"
            value={stats.totalReviews}
            icon={FileText}
            color="#2b3380"
          />
          <StatCard
            title="Completed"
            value={stats.completedReviews}
            subtitle="Reviews submitted"
            icon={CheckCircle}
            color="#4caf50"
          />
          <StatCard
            title="Pending"
            value={stats.pendingReviews}
            subtitle="Awaiting completion"
            icon={Clock}
            color="#ff9800"
          />
          <StatCard
            title="Avg Rating"
            value={`${stats.avgRating}/5`}
            icon={Star}
            color="#7e57c2"
          />
          <StatCard
            title="Active Goals"
            value={stats.activeGoals}
            subtitle={`${stats.achievedGoals} achieved`}
            icon={Target}
            color="#00897b"
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="border-b border-gray-100">
            <div className="flex gap-1 p-2">
              {[
                { id: 'reviews', label: 'Performance Reviews', icon: BarChart3 },
                { id: 'goals', label: 'Goals & KPIs', icon: Target },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                    activeTab === tab.id
                      ? 'text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  style={activeTab === tab.id ? { backgroundColor: '#2b3380' } : {}}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <div className="p-5">
              {/* Search & Filter */}
              <div className="flex flex-col md:flex-row gap-4 mb-5">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by employee name or period..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none bg-white"
                >
                  <option value="all">All Status</option>
                  <option value="Draft">Draft</option>
                  <option value="Submitted">Submitted</option>
                  <option value="Completed">Completed</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>

              {/* Reviews Table */}
              {filteredReviews.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No performance reviews found</p>
                  <p className="text-sm">Create a new review to get started</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Employee</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Review Period</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Rating</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredReviews.map((review) => {
                        const rating = review.overall_rating || 'N/A';
                        return (
                          <tr key={review.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium" style={{ backgroundColor: '#2b3380' }}>
                                  {review.employee_name?.charAt(0) || 'E'}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-800">{review.employee_name || 'Unknown'}</p>
                                  <p className="text-sm text-gray-500">{review.reviewer_name || 'No reviewer'}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <div>
                                <p className="font-medium text-gray-700">{review.review_cycle || review.review_period || '-'}</p>
                                <p className="text-sm text-gray-500">
                                  {review.created_at ? formatDate(review.created_at) : '-'}
                                </p>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <div className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg ${getRatingBg(rating)}`}>
                                <Star className={`w-4 h-4 ${getRatingColor(rating)}`} />
                                <span className={`font-bold ${getRatingColor(rating)}`}>
                                  {typeof rating === 'number' ? rating.toFixed(1) : rating}
                                </span>
                                <span className="text-gray-400">/5</span>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getStatusBadge(review.status)}`}>
                                {review.status}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-2">
                                <button className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                  <Eye className="w-4 h-4" />
                                </button>
                                {review.status === 'Draft' && (
                                  <>
                                    <button 
                                      onClick={() => handleSubmitReview(review.id)}
                                      className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </button>
                                    <button 
                                      onClick={() => handleDeleteReview(review.id)}
                                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Goals Tab */}
          {activeTab === 'goals' && (
            <div className="p-5">
              {goals.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No goals found</p>
                  <p className="text-sm">Goals help track employee progress</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {goals.map((goal) => (
                    <div key={goal.id} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="p-2 rounded-lg bg-white">
                            <Target className="w-5 h-5" style={{ color: '#2b3380' }} />
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(goal.status)}`}>
                            {goal.status}
                          </span>
                        </div>
                        <span className="text-sm text-gray-500">{goal.progress_percentage || 0}%</span>
                      </div>
                      <h3 className="font-semibold text-gray-800 mb-1">{goal.goal_title || goal.title}</h3>
                      <p className="text-sm text-gray-500 mb-3 line-clamp-2">{goal.goal_description || goal.description}</p>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full transition-all" 
                          style={{ 
                            width: `${goal.progress_percentage || 0}%`,
                            backgroundColor: '#2b3380'
                          }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Target: {goal.target_date ? formatDate(goal.target_date) : '-'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Create Review Modal */}
        {showReviewForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-800">Create Performance Review</h2>
                <button onClick={() => setShowReviewForm(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <form onSubmit={handleCreateReview} className="p-5 space-y-4 overflow-y-auto max-h-[60vh]">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                  <select
                    value={reviewFormData.employee_id}
                    onChange={(e) => setReviewFormData({...reviewFormData, employee_id: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
                    required
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.full_name || emp.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Review Period</label>
                  <select
                    value={reviewFormData.review_period}
                    onChange={(e) => setReviewFormData({...reviewFormData, review_period: e.target.value, review_cycle: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
                  >
                    <option value="Q1 2026">Q1 2026</option>
                    <option value="Q4 2025">Q4 2025</option>
                    <option value="Q3 2025">Q3 2025</option>
                    <option value="Annual 2025">Annual 2025</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Overall Rating</label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map(num => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => setReviewFormData({...reviewFormData, overall_rating: num})}
                        className={`p-2 rounded-lg border transition-all ${
                          reviewFormData.overall_rating >= num 
                            ? 'bg-yellow-100 border-yellow-300' 
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <Star className={`w-6 h-6 ${reviewFormData.overall_rating >= num ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                      </button>
                    ))}
                    <span className="ml-2 font-bold text-gray-700">{reviewFormData.overall_rating}/5</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Strengths</label>
                  <textarea
                    value={reviewFormData.strengths}
                    onChange={(e) => setReviewFormData({...reviewFormData, strengths: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
                    rows={2}
                    placeholder="Key strengths observed..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Areas for Improvement</label>
                  <textarea
                    value={reviewFormData.areas_for_improvement}
                    onChange={(e) => setReviewFormData({...reviewFormData, areas_for_improvement: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
                    rows={2}
                    placeholder="Areas that need improvement..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Comments</label>
                  <textarea
                    value={reviewFormData.reviewer_comments}
                    onChange={(e) => setReviewFormData({...reviewFormData, reviewer_comments: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
                    rows={2}
                    placeholder="Additional comments..."
                  />
                </div>
              </form>
              <div className="flex justify-end gap-3 p-5 border-t border-gray-100 bg-gray-50">
                <button
                  type="button"
                  onClick={() => setShowReviewForm(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateReview}
                  className="px-5 py-2 text-white rounded-lg flex items-center gap-2"
                  style={{ backgroundColor: '#2b3380' }}
                >
                  <Save className="w-4 h-4" />
                  Create Review
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default UnifiedPerformanceManagement;
